import React from "react";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";

type TDeleteProjectModalProps = {
    /** Truthy opens. Widened past `boolean` because InputParameterTable passes
     *  the pending-delete ID itself (string | null) — shipped usage, kept. */
    showModal: boolean | string | null;
    closeModal: () => void;
    onConfirm: () => void;
    loading?: boolean;
    deleteItem?: { id?: string; name?: string } | null;
    id?: string;
    /** The consequence sentence. Default = the model sentence every ISR/AB caller relies on; Product Cards passes its own (2026-09-09). */
    message?: string;
    /** Confirm label while `loading`; default keeps "Delete". */
    loadingText?: string;
    /** Overrides the whole heading. ISR's linked-models row asks "Unlink Model ?",
     *  which is not a delete at all — the default stays the `Delete <name>?` form
     *  every other caller relies on (release-sept-29). */
    title?: string;
    /** Overrides the confirm button's RESTING label, where `loadingText` overrides
     *  only its in-flight one. Both exist because they answer different questions:
     *  "Unlink" names the act, "Unlinking…" names the wait (release-sept-29). */
    confirmLabel?: string;
};

/**
 * The delete-model confirm on DS Dialog `tone="danger"` + primary confirm —
 * the settled destructive-confirm pattern (§ 84/§ 92). The legacy red-filled
 * FillButton and the hand-rolled ModalContainer trio are gone; Escape and
 * scrim-click now close through the same `closeModal` as Cancel. Shared by the
 * ISR/Guide list, the UI-Model table, AB testing and Input Parameters.
 *
 * **The body is prose, not an `Alert` (§ 166) — the copy is unchanged.** It was
 * `<Alert tone="danger">` inside `<Dialog tone="danger">`, which drew `CrossCircle`
 * at 20px beside the title and `CrossCircle` at 20px again 40px below it: the same
 * mark twice for one fact. And because the alert was the dialog's ONLY child, its
 * tint filled the whole content region — a tint marks a region as different from its
 * surroundings, so a tint with no surroundings just repaints the dialog.
 * `RuleChangeConfirmDialog` already recorded the rule ("passing both would render the
 * triangle twice"); this is the same rule resolved the other way round.
 */
const DEFAULT_MESSAGE = 'This will permanently delete the model. Any saved configurations referencing this model will no longer load.';

const DeleteProjectModal = ({ showModal, closeModal, onConfirm, loading = false, deleteItem, id, message = DEFAULT_MESSAGE, loadingText, title, confirmLabel }: TDeleteProjectModalProps) => {
    /*
        § 209 — while the parent's delete is in flight (`loading`; the AB, ISR
        list and UI-Model-table mounts all pass a live flag reset in `finally`)
        the dialog is COMMITTED: the delete is not abortable, and a mid-flight
        close would only hide the dialog while the toast + list refresh still
        land. One guard covers every close route (Cancel, the X, Escape —
        Dialog routes all three through `onClose`). The InputParameterTable
        mount passes `loading={false}` and is byte-identical.
    */
    const guardedClose = () => {
        if (loading) return;
        closeModal();
    };

    if (!showModal) return null;
    return (
        <div id={id || "isr-delete-confirm-modal"}>
            <Dialog
                isOpen
                onClose={guardedClose}
                title={title ?? `Delete ${deleteItem?.name ?? ''}?`}
                tone="danger"
                data-testid={id || "isr-delete-confirm-modal"}
                footer={
                    <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                        <Button
                            variant="secondary"
                            id="isr-delete-cancel-btn"
                            data-testid="isr-delete-cancel-btn"
                            disabled={loading}
                            onClick={guardedClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger-primary"
                            id="isr-delete-confirm-btn"
                            data-testid="isr-delete-confirm-btn"
                            loading={loading}
                            onClick={() => onConfirm()}
                        >
                            {loading && loadingText ? loadingText : (confirmLabel ?? 'Delete')}
                        </Button>
                    </div>
                }
            >
                <Typography
                    variant="body-primary"
                    as="p"
                    className="tw-text-ink-secondary"
                    data-testid="isr-delete-warning"
                >
                    {message}
                </Typography>
            </Dialog>
        </div>
    );
};

export default DeleteProjectModal;
